import { formatMoney, showAddToCartPreview } from "../utils/utils"

class CartMessage extends HTMLElement {
    constructor() {
        super()

        this._onTransitionEnd = this._onTransitionEnd.bind(this);
        this._isOpen = false;
        this.message = document.getElementById('cart-message')
        this.onBodyClick = this.handleBodyClick.bind(this)
        this.message.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.closest())
        this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
            closeButton.addEventListener('click', this.closest.bind(this))
        )

        this._timeout = null
    }

    /**
     * Opens the message
     */
    open() {
        this._resetTimeout();

        // If already open, reset the animation so it can appear when spammed
        if (this._isOpen) {
            this.message.classList.remove('animate', 'active');
            // Force reflow to allow re-triggering the animation
            void this.message.offsetWidth;
        }

        this._isOpen = true;
        
        // Remove any previous transitionend event listener to prevent stacking or duplicate
        this.message.removeEventListener('transitionend', this._onTransitionEnd);

        // Show modal
        this.message.classList.add('animate', 'active');
        this.message.style.display = 'block';

        // Add a new transitionend event listener to handle focus and accessibility after the animation
        this.message.addEventListener('transitionend', this._onTransitionEnd);

        // Set up an event listener on the document body to detect clicks
        document.body.addEventListener('click', this.onBodyClick);

        // Set up an event listener on the message to detect interactions and reset the timeout
        this.message.addEventListener('mousemove', this._resetTimeout.bind(this));
        this.message.addEventListener('click', this._resetTimeout.bind(this));
        this.message.addEventListener('keydown', this._resetTimeout.bind(this));
    }

    /**
     * Handles the transition ends to ensure that focus is set correctly
     */
    _onTransitionEnd() {
        // Once the transition (animation) is complete, set the focus to the message element
        this.message.focus();

        // Ensure that keyboard focus remains within the message for better accessibility
        trapFocus(this.message);
    }

    /**
     * Closes the message
     */
    close() {
        // Remove the 'active' class from the message
        this.message.classList.remove('active')
        this.message.style.display = 'none'

        // Remove the previously added click event listener from the document body, ensuring that it won't interfere with other interactions after the message is closed
        document.body.removeEventListener('click', this.onBodyClick)

        // More clean-up to prevent any stacking of event listeners
        this.message.removeEventListener('transitionend', this._onTransitionEnd);

        // Clear the timeout
        if (this._timeout) {
            clearTimeout(this._timeout)
        }

        this._isOpen = false;
    }

    /**
     * Render the contents of a component (e.g., a cart) based on a given state
     *
     * @param {Object} parsedState - The state data used to update the content
     */
    renderContents(parsedState) {
        this.cartItemKey = parsedState.key
        this.getSectionsToRender().forEach((section) => {
            document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.id],
                section.selector
            )
        })

        // Set event listeners for quantity selector
        const productType = parsedState.product_type
        const quantitySelector = document.querySelector('.quantity-selector')
        const minusButton = quantitySelector.querySelector('.quantity-selector__button.minus');
        const plusButton = quantitySelector.querySelector('.quantity-selector__button.plus');
        const quantityInput = quantitySelector.querySelector('input[type="number"]');

        if (productType === "Report") {
            quantitySelector.style.display = "none"
        }

        minusButton.addEventListener('click', () => {
            updateItemQuantity(this.cartItemKey, 'decrement')
        })

        plusButton.addEventListener('click', () => {
            updateItemQuantity(this.cartItemKey, 'increment')
        })

        quantityInput.addEventListener('input', () => {
            updateQuantity(this.cartItemKey)
        })

        // Set the initial state of the minus button based on the quantity
        const initialQuantity = document.getElementById(`quantity-input-${this.cartItemKey}`).value
        this._updateMinusButtonState(this.cartItemKey, initialQuantity)

        this.open()

        showAddToCartPreview(this.cartItemKey);
    }

    // New method to update the minus button state
    _updateMinusButtonState(itemKey, quantity) {
        const minusButton = document.getElementById(`minus-button-${itemKey}`)
        if (minusButton) {
            minusButton.disabled = quantity <= 1
        }
    }

    /**
     * Returns an array of sections that need to be rendered or updated.
     * Each section is represented by an object containing its unique ID and, optionally, a specific selector.
     *
     * @returns {Array} An array of section objects.
     */
    getSectionsToRender() {
        return [
            {
                id: 'cart-item', // id of the cart notification product
                selector: `[id="cart-item-${this.cartItemKey}"]`, // selector dynamically targets a specific product in the cart message based on the key
            }
        ]
    }

    /**
     * Parses a given HTML string, extracts the inner HTML of the element matched by the provided selector,
     * and returns it.
     *
     * @param {string} html - The HTML string to parse and extract content from.
     * @param {string} [selector=] - The CSS selector to match the desired element.
     *
     * @returns {string} The inner HTML of the matched element.
     */
    getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML
    }

    /**
     * Handles click events on the body to determine if specific actions (like closing the message) should be taken
     *
     * @param {Event} evt - The click event object
     */
    handleBodyClick(evt) {
        // Get the element that was clicked.
        const target = evt.target
        const closeButton = target.closest('.cart-message__close')

        // Check if the clicked element is NOT the message itself and is also NOT a descendant of 'cart-message'
        if ((target !== this.message && !target.closest('.cart-message')) || closeButton) {
            // If the clicked element is NOT the message itself and is also NOT a descendant of 'cart-message', close the message
            this.close()
        }
    }

    /**
     * Resets the timeout for closing the message.
     */
    _resetTimeout() {
        // Clear any existing timeout
        if (this._timeout) {
            clearTimeout(this._timeout)
        }

        // Set a new timeout to close the message after 3 seconds
        this._timeout = setTimeout(() => {
            this.close()
        }, 3000)
    }

    setActiveElement(element) {
        this.activeElement = element
    }
}

async function updateCart(itemKey, quantity) {
    const data = {
        id: itemKey,
        quantity: quantity,
    }
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    }
    return fetch('/cart/change.js', options) // Ensure this returns a promise
        .then(response => response.json())
}


function getCartTotal() {
    return new Promise((resolve, reject) => {
        $.get('/cart.js', () => {}, 'json')
        .done((cart) => {
            resolve(cart.original_total_price)
        })
        .fail((error) => {
            reject(error)
        })
    })
}

async function updateItemQuantity(itemKey, changeType) {
    const input = document.getElementById(`quantity-input-${itemKey}`)
    let quantity = parseInt(input.value, 10)

    if (changeType === 'increment') {
        quantity++
    } else if (changeType === 'decrement' && quantity > 1) {
        quantity--
    }

    // Update input value and item total price
    input.value = quantity
    updateItemTotalPrice(itemKey, quantity)

    // Update cart and wait for it to complete
    await updateCart(itemKey, quantity)

    // Fetch and display updated cart total
    await updateCartTotalDisplay()
}

async function updateCartTotalDisplay() {
    try {
        // Fetch and display updated cart total
        const totalCart = await getCartTotal()
        const cartTotalElement = document.getElementById('cart-total')
        if (cartTotalElement) {
            cartTotalElement.textContent = `Total: ${formatMoney(totalCart)}`
        }
    } catch (error) {
        console.error('Error fetching cart total:', error)
    }
}

function updateItemTotalPrice(itemKey, quantity) {
    // Get the unit price of the item
    const eachPriceElement = document.getElementById(`item-each-price-${itemKey}`)
    const eachPrice = parseFloat(eachPriceElement.textContent.replace(/[^0-9.-]+/g,""))

    // Compute the new total price for the item
    const totalPrice = eachPrice * quantity

    // Update the displayed total price for the item
    const totalPriceElement = document.getElementById(`item-price-${itemKey}`)
    totalPriceElement.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalPrice)

    const cartMessageElement = document.querySelector('cart-message')
    if (cartMessageElement) {
        cartMessageElement._updateMinusButtonState(itemKey, quantity)
    }
}

async function updateQuantity(itemKey) {
    const input = document.getElementById(`quantity-input-${itemKey}`)
    let quantity = parseInt(input.value, 10)

    if (quantity >= 1) {
        // Update item total price
        updateItemTotalPrice(itemKey, quantity)

        // Update cart and wait for it to complete
        await updateCart(itemKey, quantity)

        // After updating the cart, fetch and display updated cart total
        try {
            const totalCartElement = document.getElementById('cart-message-product__price-total')
            const totalCart = await getCartTotal()
            let totalCartInDollars = totalCart / 100
            totalCartElement.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalCartInDollars)
        } catch (error) {
            console.error('Error fetching cart total:', error)
        }
    }
}

customElements.define('cart-message', CartMessage)

